import { useCallback, useEffect, useState } from 'react';
import { AdminBlockConfig, ContentSourceType } from '../../types/homepage';

let blocks: AdminBlockConfig[] = [];
let idCounter = 1;
const listeners = new Set<() => void>();

function emit() {
    listeners.forEach((l) => l());
}

function useBlocksSnapshot() {
    const [, forceRender] = useState(0);
    useEffect(() => {
        const listener = () => forceRender((n) => n + 1);
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    }, []);
    return blocks;
}

function fakeLatency<T>(value: T, ms = 250): Promise<T> {
    return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

type MutateOpts<T = void> = { onSuccess?: (result: T) => void; onError?: (e: Error) => void };

//READ 
export function useAdminBlocks() {
    const data = useBlocksSnapshot();
    return { data, isLoading: false, error: null as Error | null };
}

//CREATE 
interface CreatePayload {
    type: string;
    title?: string;
    subtitle?: string;
    layoutStyle: string;
    enabled: boolean;
    contentSource: { type: ContentSourceType; limit?: number };
    content: any[];
    settings: Record<string, unknown>;
}

export function useCreateBlock() {
    const mutate = useCallback((payload: CreatePayload, opts?: MutateOpts<AdminBlockConfig>) => {
        fakeLatency(null).then(() => {
            const newBlock = {
                id: `block-${idCounter++}`,
                sortOrder: blocks.length + 1,
                ...payload,
            } as AdminBlockConfig;
            blocks = [...blocks, newBlock];
            emit();
            opts?.onSuccess?.(newBlock);
        });
    }, []);
    return { mutate, isPending: false };
}

//UPDATE
export function useUpdateBlock() {
    const mutate = useCallback(
        ({ id, payload }: { id: string; payload: Partial<AdminBlockConfig> }, opts?: MutateOpts) => {
            fakeLatency(null).then(() => {
                blocks = blocks.map((b) => (b.id === id ? { ...b, ...payload } : b));
                emit();
                opts?.onSuccess?.();
            });
        },
        []
    );
    return { mutate, isPending: false };
}

//DELETE 
export function useDeleteBlock() {
    const mutate = useCallback((id: string, opts?: MutateOpts) => {
        fakeLatency(null).then(() => {
            blocks = blocks.filter((b) => b.id !== id);
            emit();
            opts?.onSuccess?.();
        });
    }, []);
    return { mutate, isPending: false };
}

//TOGGLE
export function useToggleBlock() {
    const mutate = useCallback(
        ({ id, enabled }: { id: string; enabled: boolean }, opts?: MutateOpts) => {
            fakeLatency(null).then(() => {
                blocks = blocks.map((b) => (b.id === id ? { ...b, enabled } : b));
                emit();
                opts?.onSuccess?.();
            });
        },
        []
    );
    return { mutate, isPending: false };
}

//REORDER
export function useReorderBlocks() {
    const mutate = useCallback(
        ({ blocks: ordered }: { blocks: { id: string; sortOrder: number }[] }, opts?: MutateOpts) => {
            fakeLatency(null).then(() => {
                blocks = blocks.map((b) => {
                    const match = ordered.find((o) => o.id === b.id);
                    return match ? { ...b, sortOrder: match.sortOrder } : b;
                });
                emit();
                opts?.onSuccess?.();
            });
        },
        []
    );
    return { mutate, isPending: false };
}